import { useEffect, useMemo } from 'react';
import { ConfigurationSpreadsheets } from 'common/types/scalarSpreadsheet';
import {
  FirmTotalCell,
  isCellIncludedInFirmTotalCalculation,
} from 'pages/ValuationsAllocation/util/getFirmTotal/getFirmTotal';

type UseFirmTotalProps = {
  weightedShareValuesSpreadsheets: ConfigurationSpreadsheets;
  childCompanyWSVs?: ConfigurationSpreadsheets[];
  getCurrentFirmTotal: (wsvSpreadsheets: ConfigurationSpreadsheets[]) => void;
};

/**
 * Stable fingerprint of every WSV cell that contributes to `getFirmTotal` (same rules as
 * `isCellIncludedInFirmTotalCalculation`). Used as a `useEffect` dependency so we rerun
 * `getCurrentFirmTotal` when fund-level Value cells change (e.g. after a market date update)
 * instead of only when spreadsheet object references change. Previously depending on a narrow
 * subset (e.g. security-total in column C) left the header total stale because those tracked
 * cells often did not change while per-fund Value cells did.
 */
const buildFirmTotalDependencyKey = (wsvSpreadsheets: ConfigurationSpreadsheets[]): string => {
  const contributingCells = wsvSpreadsheets
    .flatMap(spreadsheet => Object.values(spreadsheet?.cells ?? {}))
    .filter((cell): cell is FirmTotalCell =>
      Boolean(cell?.isExpr && isCellIncludedInFirmTotalCalculation(cell as FirmTotalCell))
    );

  contributingCells.sort((a, b) => {
    const ax = a.x ?? 0;
    const bx = b.x ?? 0;
    if (ax !== bx) {
      return ax - bx;
    }
    const ay = a.y ?? 0;
    const by = b.y ?? 0;
    if (ay !== by) {
      return ay - by;
    }
    return String(a.alias ?? '').localeCompare(String(b.alias ?? ''));
  });

  // Single string for React deps: compared by equality only (never split). `|` separates each
  // cell token; commas already separate x/y/alias inside a token.
  return contributingCells.map(cell => `${cell.x},${cell.y},${cell.alias}:${String(cell.value)}`).join('|');
};

const useFirmTotal = ({ weightedShareValuesSpreadsheets, childCompanyWSVs, getCurrentFirmTotal }: UseFirmTotalProps) => {
  const allWsvSpreadsheets = useMemo(
    () => [weightedShareValuesSpreadsheets, ...(childCompanyWSVs ?? [])],
    [weightedShareValuesSpreadsheets, childCompanyWSVs]
  );

  const firmTotalDependencyKey = useMemo(
    () => buildFirmTotalDependencyKey(allWsvSpreadsheets),
    [allWsvSpreadsheets]
  );

  useEffect(() => {
    if (weightedShareValuesSpreadsheets) {
      getCurrentFirmTotal(allWsvSpreadsheets);
    }
  }, [weightedShareValuesSpreadsheets, getCurrentFirmTotal, firmTotalDependencyKey, allWsvSpreadsheets]);
};

export default useFirmTotal;
